import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { TUTORIAL_STEPS } from './tutorialSteps';
import { useTutorial } from './useTutorial';

const SPOTLIGHT_PADDING = 10;
const POPOVER_OFFSET = 20;
const POPOVER_WIDTH = 320;

interface TargetRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

const getTargetRect = (selector: string): TargetRect | null => {
  const el = document.querySelector(selector);
  if (!el) return null;
  const r = el.getBoundingClientRect();
  return { x: r.x, y: r.y, width: r.width, height: r.height };
};

const computePopoverPosition = (
  rect: TargetRect,
  position: string,
  windowW: number,
  windowH: number,
): React.CSSProperties => {
  const cx = rect.x + rect.width / 2;
  const cy = rect.y + rect.height / 2;
  const sp = SPOTLIGHT_PADDING;
  const safeLeft = Math.max(16, Math.min(cx - POPOVER_WIDTH / 2, windowW - POPOVER_WIDTH - 16));

  // 260 is a safe upper bound for the card height (orange bar + padding + content)
  const CARD_HEIGHT_ESTIMATE = 260;

  // Stacks the card above/below the target instead of beside it — the
  // fallback for narrow (mobile) viewports where a 320px-wide card can't
  // sit to either side of a target without covering it.
  const stackedPosition = (): React.CSSProperties => {
    const belowTop = rect.y + rect.height + sp + POPOVER_OFFSET;
    if (belowTop + CARD_HEIGHT_ESTIMATE <= windowH - 16) {
      return { top: belowTop, left: safeLeft };
    }
    const aboveTop = rect.y - sp - POPOVER_OFFSET - CARD_HEIGHT_ESTIMATE;
    return { top: Math.max(16, aboveTop), left: safeLeft };
  };

  if (position === 'right' || position === 'left') {
    const rightLeft = rect.x + rect.width + sp + POPOVER_OFFSET;
    const fitsRight = rightLeft + POPOVER_WIDTH <= windowW - 16;
    const leftLeft = rect.x - POPOVER_WIDTH - sp - POPOVER_OFFSET;
    const fitsLeft = leftLeft >= 16;

    if (position === 'right' && fitsRight) return { top: Math.max(16, cy - 80), left: rightLeft };
    if (position === 'left' && fitsLeft) return { top: Math.max(16, cy - 80), left: leftLeft };
    // Preferred side didn't fit — try the other side before giving up.
    if (fitsRight) return { top: Math.max(16, cy - 80), left: rightLeft };
    if (fitsLeft) return { top: Math.max(16, cy - 80), left: leftLeft };
    // Neither side fits (typical on mobile) — never overlap the target.
    return stackedPosition();
  }

  if (position === 'top') {
    const top = rect.y - sp - POPOVER_OFFSET - CARD_HEIGHT_ESTIMATE;
    if (top < 16) {
      // Not enough space on top — flip below, clamped so the card stays on-screen
      const bottomTop = rect.y + rect.height + sp + POPOVER_OFFSET;
      return { top: Math.min(bottomTop, windowH - CARD_HEIGHT_ESTIMATE - 16), left: safeLeft };
    }
    return { top, left: safeLeft };
  }

  // bottom (default)
  return {
    top: Math.min(rect.y + rect.height + sp + POPOVER_OFFSET, windowH - CARD_HEIGHT_ESTIMATE - 16),
    left: safeLeft,
  };
};

export const TutorialOverlay = () => {
  const {
    isActive,
    currentStep,
    currentStepIndex,
    nextStep,
    prevStep,
    skipTutorial,
    finishTutorial,
  } = useTutorial();

  const [targetRect, setTargetRect] = useState<TargetRect | null>(null);
  const [windowSize, setWindowSize] = useState({
    w: window.innerWidth,
    h: window.innerHeight,
  });

  // Recalculate the spotlight rect whenever the step changes or the window resizes.
  // A 300ms delay lets sidebar CSS transitions (duration-200) finish first.
  useEffect(() => {
    if (!isActive || !currentStep) {
      setTargetRect(null);
      return;
    }
    const timer = setTimeout(() => {
      setTargetRect(getTargetRect(currentStep.targetSelector));
    }, 300);
    return () => clearTimeout(timer);
  }, [isActive, currentStep]);

  useEffect(() => {
    const handleResize = () => {
      setWindowSize({ w: window.innerWidth, h: window.innerHeight });
      if (currentStep) {
        setTargetRect(getTargetRect(currentStep.targetSelector));
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [currentStep]);

  if (!isActive || !currentStep) return null;

  const totalSteps = TUTORIAL_STEPS.length;
  const isFirst = currentStepIndex === 0;
  const isLast = currentStepIndex === totalSteps - 1;

  const sp = SPOTLIGHT_PADDING;
  const sx = targetRect ? targetRect.x - sp : 0;
  const sy = targetRect ? targetRect.y - sp : 0;
  const sw = targetRect ? targetRect.width + sp * 2 : 0;
  const sh = targetRect ? targetRect.height + sp * 2 : 0;

  const popoverStyle: React.CSSProperties = targetRect
    ? computePopoverPosition(targetRect, currentStep.popoverPosition, windowSize.w, windowSize.h)
    : {
        top: windowSize.h / 2 - 90,
        left: Math.max(16, windowSize.w / 2 - POPOVER_WIDTH / 2),
      };

  return createPortal(
    <div className="fixed inset-0 z-[200]">
      {/* Spotlight SVG overlay — pointer-events on the dark area only */}
      <svg
        className="absolute inset-0 w-full h-full"
        style={{ display: 'block' }}
        aria-hidden="true"
      >
        <defs>
          <mask id="tutorial-spotlight-mask">
            <rect width="100%" height="100%" fill="white" />
            {targetRect && (
              <rect x={sx} y={sy} width={sw} height={sh} rx={8} fill="black" />
            )}
          </mask>
        </defs>
        <rect
          width="100%"
          height="100%"
          fill="rgba(0,0,0,0.65)"
          mask="url(#tutorial-spotlight-mask)"
        />
      </svg>

      {/* Popover card */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`Tutorial step ${currentStepIndex + 1} of ${totalSteps}`}
        className="absolute bg-white rounded-xl shadow-2xl border border-slate-200 overflow-hidden flex flex-col"
        style={{ width: POPOVER_WIDTH, ...popoverStyle }}
      >
        <div className="p-5 flex flex-col gap-3">
          {/* Title row: mascot left, step+title center, close right */}
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-center gap-3 min-w-0">
              <img
                src="/Mascot-hand.gif"
                alt=""
                aria-hidden="true"
                className="w-14 h-14 object-contain flex-shrink-0"
              />
              <div className="min-w-0">
                <span
                  className="text-xs font-semibold uppercase tracking-wide"
                  style={{ color: '#f39000' }}
                >
                  Step {currentStepIndex + 1} of {totalSteps}
                </span>
                <h3 className="mt-1 text-sm font-semibold text-slate-800">{currentStep.title}</h3>
              </div>
            </div>
            <button
              onClick={skipTutorial}
              className="text-slate-300 hover:text-slate-500 transition-colors flex-shrink-0 mt-0.5 cursor-pointer"
              aria-label="Close tutorial"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Progress dots */}
          <div className="flex gap-1.5 items-center">
            {TUTORIAL_STEPS.map((_, i) => (
              <div
                key={i}
                className="h-1.5 rounded-full transition-all duration-300"
                style={{
                  width: i === currentStepIndex ? '1rem' : '0.375rem',
                  background: i === currentStepIndex ? '#f39000' : '#e2e8f0',
                }}
              />
            ))}
          </div>

          <p className="text-sm text-slate-600 leading-relaxed">{currentStep.description}</p>

          {/* Action buttons */}
          <div className="flex items-center justify-between pt-1">
            {!isLast ? (
              <button
                onClick={skipTutorial}
                className="text-xs text-slate-400 hover:text-slate-600 transition-colors cursor-pointer"
              >
                Skip tutorial
              </button>
            ) : (
              <span />
            )}
            <div className="flex gap-2">
              {!isFirst && (
                <button
                  onClick={prevStep}
                  className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-slate-600 rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors cursor-pointer"
                >
                  <ChevronLeft className="h-3 w-3" />
                  Back
                </button>
              )}
              {isLast ? (
                <button
                  onClick={finishTutorial}
                  className="px-4 py-1.5 text-xs font-medium text-white rounded-lg transition-opacity hover:opacity-90 cursor-pointer"
                  style={{ background: '#009d3d' }}
                >
                  Finish
                </button>
              ) : (
                <button
                  onClick={nextStep}
                  className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-white rounded-lg transition-opacity hover:opacity-90 cursor-pointer"
                  style={{ background: '#009d3d' }}
                >
                  Next
                  <ChevronRight className="h-3 w-3" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};
